/**
 * De-overlaps a set of icon rects using iterative push-apart (AABB).
 *
 * Each icon is treated as an axis-aligned square of side `size`. On every
 * iteration, every overlapping pair is pushed apart along the axis of
 * minimum penetration, half each. Iterates until no overlap remains or
 * `maxIterations` is reached.
 *
 * Only the render positions are returned — source data is never mutated.
 */

export interface IconRect {
  id: string;
  /** World-space X of the icon centre. */
  x: number;
  /** World-space Y of the icon centre. */
  y: number;
  /** Side length of the icon square (world units). */
  size: number;
  /**
   * Visual scale of the icon (default 1). Used to weight push distribution:
   * larger icons act as anchors and move less while smaller icons move more.
   * A scale=2 icon absorbs 1/(2+1)=33 % of the push vs a scale=1 neighbour
   * which absorbs 67 %.
   */
  scale?: number;
}

/**
 * @param items          Icons to de-overlap.
 * @param maxIterations  Safety cap on iteration count (default 20).
 * @returns Map of id → adjusted centre position. Only icons that actually
 *          moved are present; absent ids should fall back to their original
 *          position.
 */
export function resolveIconCollisions(
  items: ReadonlyArray<IconRect>,
  maxIterations = 20,
): Map<string, { x: number; y: number }> {
  if (items.length < 2) return new Map();

  const pos = new Map<string, { x: number; y: number }>(
    items.map((item) => [item.id, { x: item.x, y: item.y }]),
  );

  for (let iter = 0; iter < maxIterations; iter++) {
    let moved = false;

    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const itemI = items[i]!;
        const itemJ = items[j]!;
        const a = pos.get(itemI.id)!;
        const b = pos.get(itemJ.id)!;
        const halfSum = (itemI.size + itemJ.size) / 2;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const overlapX = halfSum - Math.abs(dx);
        const overlapY = halfSum - Math.abs(dy);

        if (overlapX <= 0 || overlapY <= 0) continue;

        // Distribute push: the larger icon is a hard anchor and does not move
        // at all — only the smaller icon steps aside. Equal scales → 50/50.
        const scaleI = itemI.scale ?? 1;
        const scaleJ = itemJ.scale ?? 1;
        const fracA = scaleI > scaleJ ? 0 : scaleI < scaleJ ? 1 : 0.5;
        const fracB = 1 - fracA;

        // Push along the axis with the smaller penetration.
        // 0.5px margin guarantees strict separation after rounding.
        if (overlapX <= overlapY) {
          const push = overlapX + 0.5;
          const dir = dx === 0 ? 1 : Math.sign(dx);
          a.x -= fracA * push * dir;
          b.x += fracB * push * dir;
        } else {
          const push = overlapY + 0.5;
          const dir = dy === 0 ? 1 : Math.sign(dy);
          a.y -= fracA * push * dir;
          b.y += fracB * push * dir;
        }
        moved = true;
      }
    }

    if (!moved) break;
  }

  const result = new Map<string, { x: number; y: number }>();
  for (const item of items) {
    const adjusted = pos.get(item.id)!;
    if (adjusted.x !== item.x || adjusted.y !== item.y) {
      result.set(item.id, adjusted);
    }
  }
  return result;
}

/**
 * World-units "collision size" of an icon at a given zoom level.
 *
 * Designed so:
 *  - At high zoom (small iconInv), this equals the actually-visible icon size,
 *    so icons are only pushed apart when they truly overlap on screen.
 *  - At low zoom (large iconInv), the value caps out, so the on-screen spacing
 *    between de-overlapped icons stays bounded instead of growing linearly.
 *
 * Formula: min(BASE_SIZE * iconInv, CAP) * scale.
 *
 *   BASE_SIZE : the icon's base side length in world units at zoom 1 (=32).
 *   CAP       : world-unit ceiling on the collision size (clamps low-zoom growth).
 */
const BASE_SIZE = 32;
const CAP = 24;

export function iconCollisionSize(iconInv: number, scale: number): number {
  return Math.min(BASE_SIZE * iconInv, CAP) * scale;
}
